import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, Share2, Smartphone } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISSED_KEY = 'pwa-install-prompt-dismissed';
/** Tailwind `md` — install sheet is for phones, not desktop. */
const MOBILE_MQ = '(max-width: 767px)';

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export const PwaInstallPrompt: React.FC = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => localStorage.getItem(DISMISSED_KEY) === '1');
  const [isIosSafari, setIsIosSafari] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua);
    const webkit = /webkit/.test(ua);
    const notCriOS = !/crios|fxios|opios|edgios/.test(ua);

    setIsIosSafari(iOS && webkit && notCriOS);
    setInstalled(isStandalone());

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const shouldShow = useMemo(() => {
    if (!isMobileViewport) return false;
    if (dismissed || installed) return false;
    if (location.pathname === '/auth') return false;
    // Legal pages (also opened in the iOS Account webview) — never cover with install UI.
    if (location.pathname === '/privacy' || location.pathname === '/terms') {
      return false;
    }
    if (location.pathname.startsWith('/invite/')) {
      return false;
    }
    // Trainers land on Member Management; don’t cover the dashboard with install UI.
    if (
      location.pathname === '/trainer-dashboard' ||
      location.pathname.startsWith('/trainer-dashboard/')
    ) {
      return false;
    }
    return Boolean(deferredPrompt) || (isIosSafari && !isStandalone());
  }, [deferredPrompt, dismissed, installed, isIosSafari, isMobileViewport, location.pathname]);

  useEffect(() => {
    if (shouldShow) setOpen(true);
  }, [shouldShow]);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
    setOpen(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setOpen(false);
  };

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-end justify-center md:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={dismiss}
        >
          <motion.div
            className="bg-theme-bg dark:bg-theme-bg-dark rounded-t-2xl shadow-2xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.25 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 120 || info.velocity.y > 600) {
                dismiss();
              }
            }}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-9 h-1 rounded-full bg-theme-text/20 dark:bg-theme-off-white/25" />
            </div>

            <div className="px-4 pt-2 pb-[calc(2rem+env(safe-area-inset-bottom))]">
              <div className="mb-3">
                <h3 className="text-lg font-semibold text-theme-text dark:text-theme-off-white">
                  Install Bravos App
                </h3>
              </div>

              {deferredPrompt ? (
                <>
                  <p className="text-sm text-theme-text/70 dark:text-theme-off-white/70 mb-4">
                    Install Bravos for faster access from your home screen.
                  </p>
                  <button
                    type="button"
                    onClick={install}
                    className="w-full px-4 py-3.5 rounded-xl bg-primary-400 text-gray-900 hover:opacity-90 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <Download className="w-5 h-5" />
                    Install app
                  </button>
                </>
              ) : (
                <div className="rounded-xl bg-theme-muted dark:bg-white/5 border border-theme-border dark:border-white/10 p-4">
                  <p className="text-sm text-theme-text dark:text-theme-off-white font-medium mb-2">
                    iPhone install steps
                  </p>
                  <div className="space-y-2 text-sm text-theme-text/75 dark:text-theme-off-white/75">
                    <p className="flex items-center gap-2"><Share2 className="w-4 h-4" /> Tap Share in Safari</p>
                    <p className="flex items-center gap-2"><Smartphone className="w-4 h-4" /> Tap Add to Home Screen</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
